import axios from 'axios';
import { unwrapApiResponse } from './apiUtils';
import type {
  AdminAuthSession,
  AdminCreateUserPayload,
  AdminCreateUserResponse,
  AdminMeResponse,
  AdminPasswordResetRequestResponse,
  AdminUsersListResponse,
  AdminUserRecord,
} from '../types/admin';
import type { ApiResponse } from '../types/api';

const devAdminApiBaseUrl = process.env.REACT_APP_ADMIN_API_BASE_URL?.trim();
const browserDevBaseUrl =
  typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5000/api/admin`
    : 'http://localhost:5000/api/admin';
const adminApiBaseURL =
  devAdminApiBaseUrl || (process.env.NODE_ENV === 'development' ? browserDevBaseUrl : '/api/admin');

const adminApi = axios.create({
  baseURL: adminApiBaseURL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export async function loginAdmin(email: string, password: string) {
  const response = await adminApi.post<ApiResponse<AdminAuthSession>>('/auth/login', {
    email,
    password,
  });
  return unwrapApiResponse(response);
}

export async function logoutAdmin() {
  const response = await adminApi.post<ApiResponse<{ ok: boolean }>>('/auth/logout');
  return unwrapApiResponse(response);
}

export async function fetchAdminSession() {
  const response = await adminApi.get<ApiResponse<AdminAuthSession>>('/auth/session');
  return unwrapApiResponse(response);
}

export async function requestAdminPasswordReset(email: string) {
  const response = await adminApi.post<ApiResponse<AdminPasswordResetRequestResponse>>(
    '/auth/password-reset/request',
    { email }
  );
  return unwrapApiResponse(response);
}

export async function confirmAdminPasswordReset(token: string, password: string) {
  const response = await adminApi.post<ApiResponse<{ ok: boolean }>>('/auth/password-reset/confirm', {
    token,
    password,
  });
  return unwrapApiResponse(response);
}

export async function fetchAdminMe() {
  const response = await adminApi.get<ApiResponse<AdminMeResponse>>('/me');
  return unwrapApiResponse(response);
}

export async function fetchAdminUsers() {
  const response = await adminApi.get<ApiResponse<AdminUsersListResponse>>('/users');
  return unwrapApiResponse(response);
}

export async function createAdminUser(payload: AdminCreateUserPayload) {
  const response = await adminApi.post<ApiResponse<AdminCreateUserResponse>>('/users', payload);
  return unwrapApiResponse(response);
}

export async function updateAdminUserAccess(userId: string, active: boolean) {
  const response = await adminApi.patch<ApiResponse<{ user: AdminUserRecord }>>(`/users/${userId}/access`, {
    active,
  });
  return unwrapApiResponse(response);
}

export default adminApi;
